import React, { useState } from 'react'
import Table from 'react-bootstrap/Table'
import { format } from 'date-fns'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import {
  filterIsSmall,
  filterIsLarge,
  filterIsPrivate,
  filterIsActive,
  filterHasLocation,
  filterWrongHoleCount,
  filterIsZombieCourse,
  filterHasInvalidCountryAndState,
} from './CourseFilters'

const boolSort = (a, b) => a - b
const intSort = (a, b) => a - b
const stringSort = (a, b) => (a || '').localeCompare(b || '')
const undefinedSort = (a, b) => (!!a ? 1 : 0) - (!!b ? 1 : 0)
const SORT_CONFIG = {
  initialSort: [{ key: 'name', order: 'desc' }],
  columns: {
    active: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: boolSort,
    },
    priv: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: boolSort,
    },
    groupId: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: intSort,
    },
    id: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: intSort,
    },
    name: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: stringSort,
    },
    info: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: stringSort,
    },
    info2: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: stringSort,
    },
    country: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: (a, b) => {
        const as = !!a ? a.name : ''
        const bs = !!b ? b.name : ''
        return as.localeCompare(bs)
      },
    },
    state: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: (a, b) => {
        const as = a ? a.name : ''
        const bs = b ? b.name : ''
        return as.localeCompare(bs)
      },
    },
    holeCount: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: intSort,
    },
    par: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: intSort,
    },
    location: {
      filter: '',
      sortOptions: ['asc', 'desc', undefined],
      sortFn: undefinedSort,
    },
    updated: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: intSort,
    },
  },
}

const toggleStatus = (course, changes) => ({
  type: 'ADMIN_TOGGLE_COURSE_STATUS',
  payload: {
    request: {
      url: `/admin/layout/${course.id}/setState`,
      method: 'post',
      data: changes,
    },
  },
})

const Pagination = ({ items, onClick, page = 0, pageSize = 100 }) => {
  const pages = R.splitEvery(pageSize, items)
  const render = () => (
    <div>
      {pages.map((p, index) => (
        <button
          key={index}
          onClick={() => onClick(index)}
          className={
            `btn btn-sm btn-outline-` + (index === page ? 'success' : 'danger')
          }
        >
          {index + 1}
        </button>
      ))}
    </div>
  )

  return {
    current: pages[page],
    pages,
    render,
  }
}

function CourseTable({
  courses = [],
  uniqeCoursesByName = [],
  onClicked = () => {},
  groupId = null,
  initialSort = [{ key: 'name', order: 'desc' }],
}) {
  const dispatch = useDispatch()
  const [sort, setSort] = useState(initialSort)
  const [page, setPage] = useState(0)

  const renderSort = (columnName) => {
    const columnSortConfig = sort.find(({ key }) => key === columnName)
    const { order } = columnSortConfig || {}
    const upClass = 'bi bi-caret-up' + (order === 'asc' ? '-fill' : '')
    const downClass = 'bi bi-caret-down' + (order === 'desc' ? '-fill' : '')
    const columnSortPrio = sort.indexOf(columnSortConfig)

    return (
      <div className="sort-column--order">
        <div className="sort-column--order--carets">
          <i className={upClass}></i>
          <i className={downClass}></i>
        </div>
        <div className="sort-column--order--priority">
          {columnSortPrio !== -1 ? columnSortPrio + 1 : ''}
        </div>
      </div>
    )
  }

  const toggledOrder = (columnName, order) => {
    const { sortOptions } = SORT_CONFIG.columns[columnName]
    const index = sortOptions.indexOf(order)
    return sortOptions[(index + 1) % sortOptions.length]
  }

  const onSortClicked = (columnName) => {
    let tmpSort = sort

    const item = tmpSort.find(({ key }) => key === columnName)
    if (!!item) {
      tmpSort = tmpSort.filter((obj) => {
        return obj.key !== columnName
      })
    }

    const order = toggledOrder(columnName, (item || {}).order)
    if (order !== undefined) {
      tmpSort.push({
        key: columnName,
        order,
      })
    }

    setSort(tmpSort)
  }

  const onRowClicked = (course, column) => {
    onClicked(course, column)
  }

  // `admin/layout/{id}/setState` takes the two flags independently and reads null as "leave
  // alone", so each button sends only the one it is about -- the same contract as the course
  // endpoint in GroupsTable. It replaced a single `state` enum, whose fallback branch ignored the
  // column and toggled privacy for anything that was not `active`.
  const onToggleStatus = (course, status) => {
    const changes = {
      active: { active: !course.active },
      priv: { private: !course.priv },
    }[status]

    if (changes) dispatch(toggleStatus(course, changes))
  }

  const renderGroupIdLink = (groupId) => {
    return (
      <a href={`admin/${groupId}`} rel="noreferrer" target="_blank">
        {groupId}
      </a>
    )
  }

  const renderDirectionsLink = ({ lat, lng }) => {
    // TODO Add origin if available: &origin=34.1030032,-118.41046840000001
    const destination = `${lat * 0.000001},${lng * 0.000001}`
    return (
      <a // eslint-disable-line jsx-a11y/anchor-has-content
        href={`https://www.google.com/maps/dir/?api=1&destination=${destination}`}
        rel="noreferrer"
        target="_blank"
        className="bi-box-arrow-up-right"
      ></a>
    )
  }

  const courseClasses = (course) => {
    return (
      (filterIsActive(course) ? ' is-active' : ' is-inactive') +
      (filterIsPrivate(course) ? ' is-private' : ' is-public') +
      (filterIsSmall(course) ? ' is-small' : '') +
      (filterIsLarge(course) ? ' is-large' : '') +
      (filterHasLocation(course) ? ' has-location' : ' has-no-location') +
      (filterWrongHoleCount(course) ? ' has-wrong-hole-count' : '') +
      (filterIsZombieCourse(course) ? ' is-zombie' : '') +
      (filterHasInvalidCountryAndState(course) ? ' has-wrong-country' : '') +
      (uniqeCoursesByName.indexOf(course) !== -1
        ? ' is-unique'
        : ' is-non-unique')
    )
  }

  const courseStatus = (course) => {
    return (
      <div>
        {filterIsActive(course) ? (
          <i className="bi bi-bookmark-check-fill" />
        ) : (
          <i className="bi bi-bookmark" />
        )}
        {filterIsPrivate(course) ? (
          <i className="bi bi-eye-slash" />
        ) : (
          <i className="bi bi-eye-fill" />
        )}
        {filterIsSmall(course) && <i className="bi bi-thermometer" />}
        {filterIsLarge(course) && <i className="bi bi-thermometer-high" />}
        {filterHasLocation(course) && <i className="bi bi-pin-angle-fill" />}
        {filterWrongHoleCount(course) && <i className="bi bi-x-octagon" />}
        {filterIsZombieCourse(course) && <i className="bi bi-radioactive" />}
        {filterHasInvalidCountryAndState(course) && (
          <i className="bi bi-x-octagon" />
        )}
      </div>
    )
  }

  if (groupId !== null) {
    courses = courses.filter((course) => course.groupId === groupId)
  }
  if (!courses || courses.length === 0) return null

  const revSort = R.reverse(sort)
  const sortedCourses = courses.sort((a, b) => {
    for (let s of revSort) {
      const result = SORT_CONFIG.columns[s.key].sortFn(a[s.key], b[s.key])
      if (result !== 0) {
        if (s.order === 'desc') return result
        else return -result
      }
    }
    return 0
  })
  const pagination = Pagination({
    items: sortedCourses,
    page: page,
    onClick: setPage,
  })

  const th = (name, key) => (
    <th className="sort-column" onClick={() => onSortClicked(key)}>
      {name} {renderSort(key)}
    </th>
  )

  const renderDiff = (prev, next, key) => {
    if (groupId === null || prev === null || prev[key] === next[key]) {
      if (key === 'country' || key === 'state') {
        return next[key] ? next[key].name : ''
      }
      return next[key]
    }

    if (key === 'country' || key === 'state') {
      return <span className="diff">{next[key] ? next[key].name : ''}</span>
    } else {
      return <span className="diff">{next[key]}</span>
    }
  }

  const renderHoleDiff = (prev, next, holeIndex, key) => {
    if (next.holes.length <= holeIndex) return null

    const keyMap = {
      i1: 'I',
      i2: '2',
      dist: 'D',
      tee: 'S',
      basket: 'E',
    }

    const showDiff =
      groupId !== null && prev !== null && prev.holes.length > holeIndex

    const prevHole = showDiff ? prev.holes[holeIndex] : null
    const currentHole = next.holes[holeIndex]

    const hasInfo = (info) => !!info && info.trim().length > 0
    const hasDistance = (distance) => !!distance && distance !== '0'

    const hasLocation = (location) =>
      !!location && location.lat !== 0 && location.lng !== 0

    const isHoleKeyDiff = (prevHole, currentHole, key) => {
      switch (key) {
        case 'par':
        case 'i1':
        case 'i2':
        case 'dist':
          return prevHole[key] !== currentHole[key]
        case 'tee':
        case 'basket': {
          const l1 = prevHole[key]
          const l2 = currentHole[key]
          return (
            hasLocation(l1) !== hasLocation(l2) ||
            (hasLocation(l1) && (l1.lat !== l2.lat || l1.lng !== l2.lng))
          )
        }
        default:
          return false
      }
    }

    const displayValue = (key, currentHole) => {
      switch (key) {
        case 'par':
          return currentHole[key]
        case 'i1':
        case 'i2':
          return hasInfo(currentHole[key]) ? keyMap[key] : ''
        case 'dist':
          return hasDistance(currentHole[key]) ? keyMap[key] : ''
        case 'tee':
        case 'basket':
          return hasLocation(currentHole[key]) ? keyMap[key] : ''
        default:
          return ''
      }
    }

    if (showDiff && isHoleKeyDiff(prevHole, currentHole, key)) {
      return (
        <span className={'diff diff-' + key}>
          {displayValue(key, currentHole)}
        </span>
      )
    }

    return <span>{displayValue(key, currentHole)}</span>
  }

  const maxHoleCount = Math.max(
    ...pagination.current.map((c) => c.holes.length)
  )

  return (
    <>
      <Table hover className="admin courses-table table-sm">
        <thead>
          <tr>
            <th>Status</th>
            {th(`Active (${courses.filter(filterIsActive).length})`, 'active')}
            {th(`Private (${courses.filter(filterIsPrivate).length})`, 'priv')}
            {th(`Group ID`, 'groupId')}
            {th(`ID`, 'id')}
            {th(`Course / Layout`, 'name')}
            {th(`Description`, 'info')}
            {th(`Directions`, 'info2')}
            {th(`Country`, 'country')}
            {th(`State`, 'state')}
            {th(`Holes`, 'holeCount')}
            {th(`Par`, 'par')}
            {th(
              `Directions (${courses.filter(filterHasLocation).length})`,
              'location'
            )}
            {th(`Updated`, 'updated')}
            {[...Array(maxHoleCount)].map((_, i) => (
              <th key={'th' + i}>{i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pagination.current.map((course, index) => {
            const prevCourse = index > 0 ? pagination.current[index - 1] : null
            const updated = format(course.updated, 'yyyy-MM-dd')
            return (
              <tr key={course.id} className={courseClasses(course)}>
                <td>{courseStatus(course)}</td>
                <td>
                  {course.active ? (
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => onToggleStatus(course, 'active')}
                    >
                      Y
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => onToggleStatus(course, 'active')}
                    >
                      N
                    </button>
                  )}
                </td>
                <td>
                  {course.priv ? (
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => onToggleStatus(course, 'priv')}
                    >
                      Y
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => onToggleStatus(course, 'priv')}
                    >
                      N
                    </button>
                  )}
                </td>
                <td>{renderGroupIdLink(course.groupId)}</td>
                <td onClick={() => onRowClicked(course, 'id')}>{course.id}</td>
                <td onClick={() => onRowClicked(course, 'name')}>
                  {renderDiff(prevCourse, course, 'name')}
                </td>
                <td
                  onClick={() => onRowClicked(course, 'info')}
                  className="info"
                >
                  {renderDiff(prevCourse, course, 'info')}
                </td>
                <td
                  onClick={() => onRowClicked(course, 'info2')}
                  className="info"
                >
                  {renderDiff(prevCourse, course, 'info2')}
                </td>
                <td onClick={() => onRowClicked(course, 'country')}>
                  {renderDiff(prevCourse, course, 'country')}
                </td>
                <td onClick={() => onRowClicked(course, 'state')}>
                  {renderDiff(prevCourse, course, 'state')}
                </td>
                <td onClick={() => onRowClicked(course, 'holeCount')}>
                  {course.holeCount + ' ' + course.holes.length}
                </td>
                <td onClick={() => onRowClicked(course, 'par')}>
                  {renderDiff(prevCourse, course, 'par')}
                </td>
                {course.location ? (
                  <td>{renderDirectionsLink(course.location)}</td>
                ) : (
                  <td onClick={() => onRowClicked(course, 'location')} />
                )}
                <td onClick={() => onRowClicked(course, 'updated')}>
                  {updated}
                </td>
                {course.holes.map((hole, holeIndex) => (
                  <td key={'hole' + holeIndex}>
                    {renderHoleDiff(prevCourse, course, holeIndex, 'par')}
                    <br />
                    {renderHoleDiff(prevCourse, course, holeIndex, 'i1')}
                    {renderHoleDiff(prevCourse, course, holeIndex, 'i2')}
                    {renderHoleDiff(prevCourse, course, holeIndex, 'dist')}
                    {renderHoleDiff(prevCourse, course, holeIndex, 'tee')}
                    {renderHoleDiff(prevCourse, course, holeIndex, 'basket')}
                  </td>
                ))}
                {course.holes.length < 50 && (
                  <td colSpan={50 - course.holes.length} />
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
      {pagination.render()}
    </>
  )
}

export default CourseTable
